/**
 * Unified Actions Provider
 * Объединяет все действия приложения в один контекст:
 * - Wishlist actions (создание вишлистов)
 * - Item actions (создание желаний)
 * - Quick Menu actions (быстрое меню)
 * - Navigation actions (bottom menu навигация)
 *
 * Context объекты — в ./actionsContext.ts
 * Хуки-потребители — в ./useAppActions.ts
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useRouterNavigation, ROUTE_PATHS } from '@/lib';
import type { ItemPrefillData } from '@/lib';
import { usePrefillStore } from '@/stores/usePrefillStore';
import { useCurrentUser } from '@/features/user';
import { useWishlistsQuery } from '@/features/wishlist';
import type { BottomMenuItemId } from '@/config';
import type {
  WishlistActions,
  ItemActions,
  QuickMenuActions,
  NavigationActions,
  AppActionsContextValue,
} from './model/types';
import { AppActionsContext, NoWishlistDrawerContext } from './actionsContext';

interface ActionsProviderProps {
  children: React.ReactNode;
}

/**
 * Единый провайдер всех действий приложения
 */
export function ActionsProvider({ children }: ActionsProviderProps): JSX.Element {
  const navigation = useRouterNavigation();
  const { data: currentUser } = useCurrentUser();
  // ВАЖНО: Загружаем ТОЛЬКО свои вишлисты для проверки перед созданием желания
  const { data: wishlists } = useWishlistsQuery(currentUser?.id);

  const [noWishlistDrawerOpen, setNoWishlistDrawerOpen] = useState(false);
  const [pendingPrefill, setPendingPrefill] = useState<ItemPrefillData | null>(null);

  // === WISHLIST ACTIONS ===

  // Создание вишлиста из bottom menu
  const handleWishlistCreateFromMenu = useCallback(() => {
    navigation.toWishlistNew();
  }, [navigation]);

  // === ITEM ACTIONS ===

  // Создание желания из bottom menu
  const handleItemCreateFromMenu = useCallback(() => {
    if (!wishlists || wishlists.length === 0) {
      // Нет вишлистов — показываем drawer с объяснением (без prefill)
      setPendingPrefill(null);
      setNoWishlistDrawerOpen(true);
      return;
    }

    // Если мы на странице детали вишлиста — используем его ID из URL
    const wishlistIdMatch = window.location.pathname.match(/^\/wishlist\/([^/]+)(?:$|\/)/);
    const contextWishlistId = wishlistIdMatch?.[1];

    if (contextWishlistId && contextWishlistId !== 'new') {
      // Проверяем, что это наш вишлист (не чужой)
      const isOwnWishlist = wishlists.some(w => w.id === contextWishlistId);
      if (isOwnWishlist) {
        navigation.toItemNew(contextWishlistId);
        return;
      }
    }

    // Если один вишлист — автовыбор
    if (wishlists.length === 1) {
      navigation.toItemNew(wishlists[0].id);
      return;
    }

    // Несколько вишлистов, не на странице вишлиста — создание без предвыбранного
    navigation.navigate(ROUTE_PATHS.ITEM_NEW_GENERIC);
  }, [wishlists, navigation]);

  // Добавление чужого желания / товара из подборки — с проверкой наличия вишлиста
  const handleAddPrefilled = useCallback((prefillData: ItemPrefillData) => {
    if (!wishlists || wishlists.length === 0) {
      // Нет вишлистов — сохраняем данные и показываем drawer
      setPendingPrefill(prefillData);
      setNoWishlistDrawerOpen(true);
      return;
    }
    // Вишлист есть — сразу открываем форму
    usePrefillStore.getState().setPrefillData(prefillData);
    navigation.navigate(ROUTE_PATHS.ITEM_NEW_PREFILLED);
  }, [wishlists, navigation]);

  // === QUICK MENU ACTIONS ===

  // Переход к поиску друзей
  const handleFindFriend = useCallback(() => {
    navigation.switchToCommunity();
  }, [navigation]);

  // Переход к созданию игры Тайный санта
  const handleSecretSanta = useCallback(() => {
    navigation.toSecretSantaNew();
  }, [navigation]);

  // === NAVIGATION ACTIONS ===

  // Обработка клика по пункту bottom menu
  const handleMenuItemChange = useCallback((menuItemId: BottomMenuItemId) => {
    switch (menuItemId) {
      case 'home':
        navigation.switchToHomeFeed();
        break;
      case 'community':
        navigation.switchToCommunity();
        break;
      case 'wishlist':
        navigation.switchToMyWishlists();
        break;
      case 'profile':
        navigation.switchToProfile();
        break;
    }
  }, [navigation]);

  // === ОБЪЕДИНЯЕМ ВСЕ ДЕЙСТВИЯ ===

  const wishlistActions = useMemo<WishlistActions>(() => ({
    createFromMenu: handleWishlistCreateFromMenu,
  }), [handleWishlistCreateFromMenu]);

  const itemActions = useMemo<ItemActions>(() => ({
    createFromMenu: handleItemCreateFromMenu,
    addPrefilled: handleAddPrefilled,
  }), [handleItemCreateFromMenu, handleAddPrefilled]);

  const quickMenuActions = useMemo<QuickMenuActions>(() => ({
    findFriend: handleFindFriend,
    secretSanta: handleSecretSanta,
  }), [handleFindFriend, handleSecretSanta]);

  const navigationActions = useMemo<NavigationActions>(() => ({
    handleMenuItemChange,
  }), [handleMenuItemChange]);

  const value = useMemo<AppActionsContextValue>(() => ({
    wishlist: wishlistActions,
    item: itemActions,
    quickMenu: quickMenuActions,
    navigation: navigationActions,
  }), [wishlistActions, itemActions, quickMenuActions, navigationActions]);

  const noWishlistDrawerValue = useMemo(() => ({
    open: noWishlistDrawerOpen,
    setOpen: setNoWishlistDrawerOpen,
    pendingPrefill,
    clearPendingPrefill: () => setPendingPrefill(null),
  }), [noWishlistDrawerOpen, pendingPrefill]);

  return (
    <AppActionsContext.Provider value={value}>
      <NoWishlistDrawerContext.Provider value={noWishlistDrawerValue}>
        {children}
      </NoWishlistDrawerContext.Provider>
    </AppActionsContext.Provider>
  );
}
